import {
  AssetDtoType,
  ReportResponseDto,
  ReportResponseDtoStatus,
} from '@api/WhisApi/generated';
import { Button } from '@components/Button/Button';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { ReportDetailDiagnosticProcedures } from '@screens/ReportDetail/components/ReportDetailDiagnosticProcedures';
import { ReportDetailGeneralInformation } from '@screens/ReportDetail/components/ReportDetailGeneralInformation';
import { ReportDetailHeader } from '@screens/ReportDetail/components/ReportDetailHeader';
import { ReportDetailSurveillance } from '@screens/ReportDetail/components/ReportDetailSurveillance';
import { ReportDetailRraSection } from '@screens/ReportDetail/components/rra/ReportDetailRraSection';
import { formatDateForLocale } from '@screens/ReportDetail/reportDetail.utils';
import { XStack, YStack } from 'tamagui';
import { ReportDetailSupportingDocuments } from './ReportDetailSupportingDocuments/ReportDetailSupportingDocuments';

interface ReportDetailProps {
  report: ReportResponseDto;
  defaultOpen?: boolean;
  onShare?: () => void;
  onCopyShareUrl?: () => void;
}

export default function ReportDetail({
  report,
  defaultOpen = false,
  onShare,
  onCopyShareUrl,
}: ReportDetailProps): React.ReactNode {
  const { language, t } = useAppTranslation();

  const isDraftStatus = report.status === ReportResponseDtoStatus.draft;
  const shouldShowRraSection =
    report.status === ReportResponseDtoStatus.pendingRRA ||
    report.status === ReportResponseDtoStatus.publishedRRA;
  const supportingAssets = report.assets.filter(
    (asset) => !asset.type || asset.type === AssetDtoType.supportingDocument,
  );
  const rraAsset =
    report.assets.find((asset) => asset.type === AssetDtoType.rra) ?? null;

  const reportAuthor =
    [report.user.firstName, report.user.lastName].filter(Boolean).join(' ') ||
    report.user.email ||
    '...';

  const creationDate = formatDateForLocale(report.createdAt, language);
  const country =
    report.country?.name?.[language] ?? report.country?.code ?? '...';

  return (
    <YStack
      width="100%"
      maxWidth="$compactPageWidth"
      alignSelf="center"
      gap="$true"
    >
      <ReportDetailHeader
        code={report.code}
        status={report.status}
        author={reportAuthor}
        creationDate={creationDate}
        country={country}
        reportId={report.id}
        versionNumber={report.versionNumber}
      />
      {(onShare || onCopyShareUrl) && (
        <XStack gap="$2" alignItems="center">
          {onShare && (
            <Button
              title={t('reportDetail.actions.share')}
              variant="outlined"
              onPress={onShare}
              icon={<Ionicons name="share-outline" size={16} />}
              flex={1}
            />
          )}
          {onCopyShareUrl && (
            <Button
              variant="outlined"
              onPress={onCopyShareUrl}
              accessibilityLabel={t('reportDetail.actions.copyShareUrl')}
              icon={<Ionicons name="copy-outline" size={16} />}
              padding="$2"
            />
          )}
        </XStack>
      )}

      <ReportDetailGeneralInformation
        report={report}
        isDraftStatus={isDraftStatus}
        defaultOpen={defaultOpen}
      />

      <ReportDetailSurveillance
        report={report}
        isDraftStatus={isDraftStatus}
        defaultOpen={defaultOpen}
      />
      <ReportDetailDiagnosticProcedures
        procedures={report.diagnosticProcedures}
        isDraftStatus={isDraftStatus}
        defaultOpen={defaultOpen}
      />

      <ReportDetailSupportingDocuments
        assets={supportingAssets}
        defaultOpen={defaultOpen}
      />
      {shouldShowRraSection && (
        <ReportDetailRraSection
          status={report.status}
          rraAsset={rraAsset}
          defaultOpen={defaultOpen}
        />
      )}
    </YStack>
  );
}
